import React, { useCallback, useRef, useImperativeHandle, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import FileItem from './UploadFileItem';
import UploadTrigger from './UploadTrigger';
import { ajaxUpload } from './utils/ajaxUpload';
import { guid, useClassNames } from 'rsuite/utils';

const getFiles = (
  event
) => {
  if (typeof event?.['dataTransfer'] === 'object') {
    return event?.['dataTransfer']?.files;
  }
  if (event.target) {
    return event.target['files'];
  }
  return [];
};

const createFile = (file) => {
  const { fileKey } = file;
  return {
    ...file,
    fileKey: fileKey || guid(),
    progress: 0
  };
};


function fileListReducer(files, action) {
  switch (action.type) {
    // Add one or more files
    case 'push':
      return [...files, ...action.files];

    // Remove a file by `fileKey`
    case 'remove':
      return files.filter(f => f.fileKey !== action.fileKey);

    // Update a file
    case 'updateFile':
      return files.map(file => {
        return file.fileKey === action.file.fileKey ? action.file : file;
      });

    // Initialization file list
    case 'init':
      return (
        action.files?.map(file => {
          // The state of the file needs to be preserved when the `fileList` is controlled.
          return files.find(f => f.fileKey === file.fileKey) || createFile(file);
        }) || []
      );
    default:
      throw new Error();
  }
}

const useFileList = (
  defaultFileList = []
) => {
  const fileListRef = useRef(defaultFileList.map(createFile));
  const fileListUpdateCallback = useRef();

  const [fileList, dispatch] = useReducer(fileListReducer, fileListRef.current);
  fileListRef.current = fileList;

  useEffect(() => {
    fileListUpdateCallback.current?.(fileList);
    fileListUpdateCallback.current = null;

    return () => {
      fileListUpdateCallback.current = null;
    };
  }, [fileList]);

  const dispatchCallback = useCallback((action, callback) => {
    dispatch(action);
    fileListUpdateCallback.current = callback;
  }, []);

  return [fileListRef, dispatchCallback];
};

const Uploader = React.forwardRef((props, ref) => {
  const {
    as: Component = 'div',
    classPrefix = 'uploader',
    className,
    listType = 'text',
    defaultFileList,
    fileList: fileListProp,
    fileListVisible = true,
    locale: localeProp,
    style,
    draggable,
    name = 'file',
    multiple = false,
    disabled = false,
    readOnly,
    plaintext,
    accept,
    children,
    toggleAs,
    removable = true,
    disabledFileItem,
    maxPreviewFileSize,
    autoUpload = true,
    action,
    headers,
    withCredentials = false,
    disableMultipart,
    timeout = 0,
    data = {},
    onRemove,
    onUpload,
    shouldUpload,
    shouldQueueUpdate,
    renderFileInfo,
    renderThumbnail,
    onPreview,
    onChange,
    onSuccess,
    onError,
    onProgress,
    onReupload,
    ...rest
  } = props;
  const { merge, withClassPrefix, prefix } = useClassNames(classPrefix);
  const classes = merge(className, withClassPrefix(listType, { draggable }));

  const rootRef = useRef();
  const xhrs = useRef({});
  const trigger = useRef();

  const [fileList, dispatch] = useFileList(fileListProp || defaultFileList);

  useEffect(() => {
    if (typeof fileListProp !== 'undefined') {
      // Force reset fileList in reducer, when `fileListProp` is updated
      dispatch({ type: 'init', files: fileListProp });
    }
  }, [dispatch, fileListProp]);

  const updateFileStatus = useCallback(
    (nextFile) => {
      dispatch({ type: 'updateFile', file: nextFile });
    },
    [dispatch]
  );

  /**
   * Clear the value in input.
   */
  const cleanInputValue = useCallback(() => {
    trigger.current?.clearInput();
  }, []);

  /**
   * Callback for successful file upload.
   * @param file
   * @param response
   * @param event
   * @param xhr
   */
  const handleAjaxUploadSuccess = useCallback(
    (file, response, event, xhr) => {
      const nextFile = {
        ...file,
        status: 'finished',
        progress: 100
      };
      updateFileStatus(nextFile);
      onSuccess?.(response, nextFile, event, xhr);
    },
    [onSuccess, updateFileStatus]
  );

  /**
   * Callback for file upload error.
   * @param file
   * @param status
   * @param event
   * @param xhr
   */
  const handleAjaxUploadError = useCallback(
    (file, status, event, xhr) => {
      const nextFile = {
        ...file,
        status: 'error'
      };
      updateFileStatus(nextFile);
      onError?.(status, nextFile, event, xhr);
    },
    [onError, updateFileStatus]
  );

  /**
   * Callback for file upload progress update.
   * @param file
   * @param percent
   * @param event
   * @param xhr
   */
  const handleAjaxUploadProgress = useCallback(
    (file, percent, event, xhr) => {
      const nextFile = {
        ...file,
        status: 'uploading',
        progress: percent
      };

      updateFileStatus(nextFile);
      onProgress?.(percent, nextFile, event, xhr);
    },
    [onProgress, updateFileStatus]
  );

  /**
   * Upload a single file.
   * @param file
   */
  const handleUploadFile = useCallback(
    (file) => {
      const { xhr, data: uploadData } = ajaxUpload({
        name,
        timeout,
        headers,
        data,
        withCredentials,
        disableMultipart,
        file: file.blobFile,
        url: action,
        onError: handleAjaxUploadError.bind(null, file),
        onSuccess: handleAjaxUploadSuccess.bind(null, file),
        onProgress: handleAjaxUploadProgress.bind(null, file)
      });

      updateFileStatus({ ...file, status: 'uploading' });

      if (file.fileKey) {
        xhrs.current[file.fileKey] = xhr;
      }

      onUpload?.(file, uploadData, xhr);
    },
    [
      action,
      data,
      handleAjaxUploadError,
      handleAjaxUploadProgress,
      handleAjaxUploadSuccess,
      headers,
      name,
      onUpload,
      timeout,
      updateFileStatus,
      withCredentials,
      disableMultipart
    ]
  );

  const handleAjaxUpload = useCallback(() => {
    fileList.current.forEach(file => {
      const checkState = shouldUpload?.(file);

      if (checkState instanceof Promise) {
        checkState.then(res => {
          if (res) {
            handleUploadFile(file);
          }
        });
        return;
      } else if (checkState === false) {
        return;
      }

      if (file.status === 'inited') {
        handleUploadFile(file);
      }
    });

    cleanInputValue();
  }, [cleanInputValue, fileList, handleUploadFile, shouldUpload]);

  const handleUploadTriggerChange = (event) => {
    const files = getFiles(event);
    const newFileList = [];

    Array.from(files).forEach((file) => {
      newFileList.push({
        blobFile: file,
        name: file.name,
        status: 'inited',
        fileKey: guid()
      });
    });

    const nextFileList = [...fileList.current, ...newFileList];
    const checkState = shouldQueueUpdate?.(nextFileList, newFileList);

    if (checkState === false) {
      cleanInputValue();
      return;
    }

    const upload = () => {
      onChange?.(nextFileList);
      dispatch({ type: 'push', files: newFileList }, () => {
        autoUpload && handleAjaxUpload();
      });
    };

    if (checkState instanceof Promise) {
      checkState.then(res => {
        res && upload();
      });
      return;
    }

    upload();
  };

  const handleRemoveFile = (fileKey) => {
    const file = find(fileList.current, f => f.fileKey === fileKey);
    const nextFileList = fileList.current.filter(f => f.fileKey !== fileKey);

    if (xhrs.current?.[file.fileKey]?.readyState !== 4) {
      xhrs.current[file.fileKey]?.abort();
    }

    dispatch({ type: 'remove', fileKey });

    onRemove?.(file);
    onChange?.(nextFileList);
    cleanInputValue();
  };

  const handleReupload = (file) => {
    autoUpload && handleUploadFile(file);
    onReupload?.(file);
  };

  // public API
  const start = (file) => {
    if (file) {
      handleUploadFile(file);
      return;
    }
    handleAjaxUpload();
  };

  useImperativeHandle(ref, () => ({
    root: rootRef.current,
    start
  }));

  const renderList = [
    <UploadTrigger
      {...rest}
      name={name}
      key="trigger"
      multiple={multiple}
      draggable={draggable}
      disabled={disabled}
      readOnly={readOnly}
      accept={accept}
      ref={trigger}
      onChange={handleUploadTriggerChange}
      as={toggleAs}
    >
      {children}
    </UploadTrigger>
  ];

  if (fileListVisible) {
    renderList.push(
      <div key="items" className={prefix('file-items')}>
        {fileList.current.map((file, index) => (
          <FileItem
            key={file.fileKey || index}
            file={file}
            maxPreviewFileSize={maxPreviewFileSize}
            listType={listType}
            disabled={disabledFileItem}
            onPreview={onPreview}
            onReupload={handleReupload}
            onCancel={handleRemoveFile}
            renderFileInfo={renderFileInfo}
            renderThumbnail={renderThumbnail}
            removable={removable && !readOnly && !plaintext}
            allowReupload={!readOnly && !plaintext}
          />
        ))}
      </div>
    );
  }

  if (plaintext) {
    return (
      <div className={withClassPrefix(listType)}>
        {fileList.current.length ? renderList[1] : null}
      </div>
    );
  }

  if (listType === 'picture') {
    renderList.reverse();
  }

  return (
    <Component ref={rootRef} className={classes} style={style}>
      {renderList}
    </Component>
  );
});

Uploader.displayName = 'Uploader';
Uploader.propTypes = {
  action: PropTypes.string.isRequired,
  accept: PropTypes.string,
  autoUpload: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  classPrefix: PropTypes.string,
  defaultFileList: PropTypes.array,
  fileList: PropTypes.array,
  data: PropTypes.object,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledFileItem: PropTypes.bool,
  name: PropTypes.string,
  timeout: PropTypes.number,
  withCredentials: PropTypes.bool,
  headers: PropTypes.object,
  listType: PropTypes.oneOf(['text', 'picture-text', 'picture']),
  shouldQueueUpdate: PropTypes.func,
  shouldUpload: PropTypes.func,
  onChange: PropTypes.func,
  onUpload: PropTypes.func,
  onReupload: PropTypes.func,
  onPreview: PropTypes.func,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  onProgress: PropTypes.func,
  onRemove: PropTypes.func,
  maxPreviewFileSize: PropTypes.number,
  style: PropTypes.object,
  renderFileInfo: PropTypes.func,
  renderThumbnail: PropTypes.func,
  removable: PropTypes.bool,
  fileListVisible: PropTypes.bool,
  draggable: PropTypes.bool,
  disableMultipart: PropTypes.bool
};

export default Uploader;
